/**
 * lib/sessionIdle.ts
 * ------------------------------------------------------------------
 * Signing out after inactivity: the rule, kept apart from AuthContext so it
 * can be tested.
 *
 * THE SCHOOL'S RULE: 30 minutes for STAFF, dropping to 10. Parents are not signed out for
 * inactivity at all — they keep using the portal as they always have.
 *
 * The difference is what an unattended screen exposes. A staff screen holds
 * the roll, marks, fees or pay; a parent's holds their own children, which
 * they may look at for as long as they like. Anyone holding a staff role is
 * held to the staff limit even if they are also a parent — the limit follows
 * the most sensitive access, not the friendliest.
 *
 * WHY IT IS STORED, NOT TIMED. It used to be a two-hour setTimeout inside the
 * page, which had three holes: a browser closed and reopened days later was
 * still signed in (nothing remembered when activity stopped); an idle tab
 * signed the user out while they worked in another (tabs did not share
 * activity); and phones and sleeping laptops pause timers. Now the last
 * activity is written to localStorage — shared by every tab — and compared
 * with the clock whenever the portal opens, a tab returns, and every few
 * seconds. The clock, not a timer, decides.
 *
 * What this cannot do is stop someone who edits their own browser storage.
 * It is a guard against the unattended screen, which is the real risk; the
 * server-side answer for sensitive roles is a second factor (SMS), not this.
 * ------------------------------------------------------------------
 */

/**
 * How long a staff screen may sit untouched before it signs itself out.
 *
 * STARTING AT 30 MINUTES ON PURPOSE (21 Sep 2026). The school is moving to
 * 10, but half the staff are still learning their new passwords, and a
 * ten-minute limit on day one means people locked out mid-lesson and a queue
 * at the office. Change this ONE number to `10 * 60 * 1000` in a week — the
 * test below states the current figure, so the change is deliberate and
 * visible rather than a quiet drift.
 */
export const STAFF_IDLE_MS = 30 * 60 * 1000;
/** Parents and pupils: no inactivity sign-out. Infinity rather than a very
 *  large number, so `Number.isFinite` is the honest test everywhere and
 *  nobody has to wonder whether 999 hours means "never". */
export const NO_IDLE_LIMIT = Number.POSITIVE_INFINITY;

/** The key every tab reads and writes. */
export const ACTIVITY_KEY = 'lkc.session.activity';

const NON_STAFF = new Set(['student', 'parent']);

/**
 * A staff role anywhere means the staff limit; parents and pupils have none.
 *
 * No roles yet — a profile that has not loaded, or failed to — takes the
 * staff limit. The safe default when we do not yet know who somebody is, is
 * to treat them as staff.
 */
export function idleLimitFor(roles: readonly string[]): number {
  if (!roles.length) return STAFF_IDLE_MS;
  return roles.every((r) => NON_STAFF.has(r)) ? NO_IDLE_LIMIT : STAFF_IDLE_MS;
}

/** How long before sign-out the warning shows. Nothing to warn about where
 *  there is no limit. */
export function warningFor(limitMs: number): number {
  if (!Number.isFinite(limitMs)) return 0;
  return limitMs <= STAFF_IDLE_MS ? 60 * 1000 : 2 * 60 * 1000;
}

/* ---- holds: a screen that must not sign out while it is doing its job ----
 * From Al-Nur (30 Oct 2026). The exam room screen is projected and nobody
 * touches it for an hour, so the staff limit would sign it out mid-paper. A
 * screen HOLDS the session while it matters and releases it when it stops
 * mattering (every paper finished). A hold counts as activity, so other tabs
 * stay signed in too. */
const holds = new Set<string>();

/** Hold the session open; returns the release. */
export function holdSession(key: string): () => void {
  holds.add(key);
  return () => { holds.delete(key); };
}

export function sessionHeld(): boolean {
  return holds.size > 0;
}

export type IdleState = 'active' | 'warning' | 'expired';

export function idleState(now: number, lastActivity: number, limitMs: number): IdleState {
  // No limit: never warns, never expires, however long the tab sits open.
  if (!Number.isFinite(limitMs)) return 'active';
  const idle = now - lastActivity;
  if (idle >= limitMs) return 'expired';
  if (idle >= limitMs - warningFor(limitMs)) return 'warning';
  return 'active';
}

interface Stored { uid: string; at: number }

/** The last activity recorded for this user on this device, or null. */
export function readActivity(uid: string, storage: Pick<Storage, 'getItem'> | null = safeStorage()): number | null {
  try {
    const raw = storage?.getItem(ACTIVITY_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Stored>;
    return v.uid === uid && typeof v.at === 'number' ? v.at : null;
  } catch {
    return null;
  }
}

export function writeActivity(uid: string, at: number, storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try {
    storage?.setItem(ACTIVITY_KEY, JSON.stringify({ uid, at }));
  } catch {
    /* private mode or full storage: the in-page clock still applies */
  }
}

export function clearActivity(storage: Pick<Storage, 'removeItem'> | null = safeStorage()): void {
  try { storage?.removeItem(ACTIVITY_KEY); } catch { /* ignore */ }
}

function safeStorage(): Storage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}
